import { useCallback, useEffect, useState } from 'react';
import { observer } from '@legendapp/state/react';
import { calculatePrice, getProductUrl } from '@kidslabs/module-catalog';
import { AddToCartView } from './add-to-cart-view';
import { AddToCartErrorTypeEnum } from '../../enums';
import {
    useAttachedCartItemReferenceLogics,
    useCartActionsV3,
    useCartItemReference,
    useAuthenticationGuard,
    useBuyNowAction,
} from '../../hooks';
import { cartItemReferenceStoreAPI } from '../../stores/cart-item-reference-store.public-api';
import { generateWebCartItem, isProductInStock } from '../../models';
import { HOTLINE_NUMBER, MIN_CART_ITEM_QUANTITY } from '../../constants';
import type { AddToCartProps } from './add-to-cart.types';
import { useRouter } from 'next/navigation';
import { getCartItem, getProductId } from '../../utils/cart.utils';
import { useAddToCartValidation } from '../../hooks/use-add-to-cart-validation';
import { useCartAnimation } from '../../hooks/use-cart-animation';

export const AddToCart = observer(
    ({
        product,
        variant = 'quick',
        variation,
        disabled = false,
        emitErrorState,
        onFullButtonClick,
        onBuyNowClick,
        authenticationGuard,
    }: AddToCartProps) => {
        const router = useRouter();

        const { getAttachedCartItemReferenceLogics, clearAttachedCartItemReferenceLogics } =
            useAttachedCartItemReferenceLogics();
        const { getQuantityStepper } = cartItemReferenceStoreAPI.state;
        const {
            itemByProductIdWithoutGifts$,
            handleIncreaseQuantity,
            handleChangeQuantity,
            loading,
            errorMessage,
            setQuantityStepper,
        } = useCartActionsV3();
        const { getCartItemReferencesByProductId } = useCartItemReference();

        const { handleBuyNowSingle, loading: buyNowLoading } = useBuyNowAction();

        // Authentication guard - only active if config is provided
        const { requireAuthentication } = useAuthenticationGuard(
            authenticationGuard ?? { isAuthenticated: true, openLoginModal: () => {} }
        );

        const [quantity, setQuantity] = useState<number>(1);
        const item = generateWebCartItem(product, variation);

        // ✅ FIX: Find draft cart item by product ID (not by item.id which might not exist yet)
        const productId = getProductId(product, variation);

        const [errorAddToCart, setErrorAddToCart] = useState<string>();

        const productPrice = product.price_range?.minimum_price?.final_price?.value;
        const { price } = calculatePrice({ amount: productPrice ?? 0 });

        // ✅ FIX: Calculate total quantity from cart item references (single source of truth)
        const cartItemReferences = productId ? getCartItemReferencesByProductId(productId) : [];
        const cartQty = cartItemReferences.reduce((total, ref) => total + ref.qty, 0);

        const existingCartItem = getCartItem(productId ?? undefined, itemByProductIdWithoutGifts$);
        const outOfStock = Boolean(existingCartItem && !isProductInStock(item.product));

        const handleCheckStock = async (): Promise<boolean> => {
            if (outOfStock) {
                //Handle dialog when out of stock here
                return false;
            }
            return true;
        };

        const handleDecreaseQuantity = () => {
            setQuantity(prev => {
                if (prev > MIN_CART_ITEM_QUANTITY) {
                    return prev - 1;
                }
                return prev;
            });
        };

        const navigateToProductDetail = () => {
            const productUrl = getProductUrl(product, false);
            router.push(productUrl);
        };

        const { validateAddToCart } = useAddToCartValidation({
            productTypeName: product.__typename,
            variation,
        });

        const { runCartAnimation } = useCartAnimation({
            variant,
        });

        const handleCallNow = useCallback(() => {
            if (typeof window === 'undefined') return;

            try {
                window.location.href = `tel:${HOTLINE_NUMBER}`;
            } catch (error) {
                console.error('[AddToCart] Call now failed:', error);
                window.alert(`Không thể thực hiện cuộc gọi tự động. Vui lòng gọi hotline ${HOTLINE_NUMBER}.`);
            }
        }, []);

        const handleAddClick = async (event?: React.MouseEvent<HTMLButtonElement>) => {
            const cartItemReferenceLogics = getAttachedCartItemReferenceLogics();
            const valueToAdd = getQuantityStepper() ?? quantity;
            const newItemToAdd = {
                ...item,
                quantity: valueToAdd,
            };
            handleIncreaseQuantity({
                parentProduct: product.__typename === 'ConfigurableProduct' ? product : undefined,
                item: newItemToAdd,
                value: valueToAdd,
                cartItemReferenceLogics,
            });

            runCartAnimation(event);
            setQuantityStepper(1);
            clearAttachedCartItemReferenceLogics();
        };

        const fullButtonClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
            if (onFullButtonClick && typeof onFullButtonClick === 'function') {
                onFullButtonClick({
                    handleAddClick: () => {
                        void handleAddClick(event);
                    },
                });

                return;
            }

            void handleAddClick(event);
        };

        const handleAddClickWithAnimation = (event: React.MouseEvent<HTMLButtonElement>) => {
            // Guest users CAN add to cart without authentication.
            // Authentication is only required for Buy Now / checkout actions.

            if (!validateAddToCart()) {
                navigateToProductDetail();
                emitErrorState?.(AddToCartErrorTypeEnum.MISSING_SELECTED_VARIANT);
                return;
            }
            // For 'full' variant with onFullButtonClick (e.g., gift selector), use fullButtonClick
            // For other variants, directly call handleAddClick
            if ((variant === 'full' || variant === 'counter') && onFullButtonClick) {
                fullButtonClick(event);
                return;
            }
            void handleAddClick(event);
        };

        const handleBuyNowClick = async () => {
            // Check authentication BEFORE proceeding
            if (authenticationGuard && !requireAuthentication()) {
                return;
            }

            if (!validateAddToCart()) {
                navigateToProductDetail();
                emitErrorState?.(AddToCartErrorTypeEnum.MISSING_SELECTED_VARIANT);
                return;
            }

            const isAvailableForSale = await handleCheckStock();
            if (!isAvailableForSale) {
                return;
            }

            const executeBuyNow = async () => {
                try {
                    const cartItemReferenceLogics = getAttachedCartItemReferenceLogics();
                    const valueToAdd = getQuantityStepper() ?? quantity;
                    await handleBuyNowSingle({
                        item,
                        quantity: valueToAdd,
                        cartItemReferenceLogics,
                        parentProduct: product.__typename === 'ConfigurableProduct' ? product : undefined,
                    });
                    clearAttachedCartItemReferenceLogics();
                } catch (error) {
                    console.error('[AddToCart] Buy now failed:', error);
                }
            };

            // If onBuyNowClick is provided, delegate to caller (e.g., flash-sale gift selector)
            if (onBuyNowClick && typeof onBuyNowClick === 'function') {
                onBuyNowClick({ handleBuyNow: executeBuyNow });
                return;
            }

            await executeBuyNow();
        };

        useEffect(() => {
            setErrorAddToCart(errorMessage);
        }, [errorMessage]);

        return (
            <AddToCartView
                productPrice={price}
                quantity={cartQty}
                variant={variant}
                loading={loading || buyNowLoading}
                disable={disabled}
                onCallNow={handleCallNow}
                onAddClick={handleAddClickWithAnimation}
                onBuyNow={handleBuyNowClick}
                onChangeQuantity={handleChangeQuantity}
                onDecreaseQuantity={handleDecreaseQuantity}
                onIncreaseQuantity={handleIncreaseQuantity}
                errorMessage={errorAddToCart}
            />
        );
    }
);
